/**
 * Prompt Template Context
 * Provides prompt template data and governance functions to the application
 */

import React, { createContext, useContext, ReactNode, useState, useEffect } from "react";
import { PromptTemplate, PromptTemplateVersion } from "../models/PromptTemplate";
import {
  getPromptTemplates,
  createPromptTemplate,
  updatePromptTemplate,
  deletePromptTemplate,
  getTemplateVersions,
  createTemplateVersion,
  updateVersionStatus,
  type VersionStatus,
} from "../api/api";

/** Logs errors only in non-production environments to avoid leaking sensitive details. */
const logError = (message: string, error: unknown): void => {
  if (process.env.NODE_ENV !== 'production') {
    console.error(message, error);
  }
};

interface PromptTemplateContextType {
  templates: PromptTemplate[];
  canManagePrompts: boolean;
  loading: boolean;
  reloadTemplates: () => Promise<void>;
  createTemplate: (template: Omit<PromptTemplate, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateTemplate: (template: PromptTemplate) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  getTemplatesByCategory: (category: string) => PromptTemplate[];
  findTemplateById: (id: string) => PromptTemplate | undefined;
  searchTemplates: (query: string) => PromptTemplate[];
  // Version operations
  getVersions: (templateId: string) => Promise<PromptTemplateVersion[]>;
  createVersion: (templateId: string, content: string, changeDescription?: string) => Promise<PromptTemplateVersion | null>;
  changeVersionStatus: (versionId: string, status: VersionStatus, reason?: string) => Promise<boolean>;
}

const PromptTemplateContext = createContext<PromptTemplateContextType | undefined>(
  undefined
);

interface PromptTemplateProviderProps {
  children: ReactNode;
}

/**
 * Provider component for Prompt Templates
 */
export const PromptTemplateProvider: React.FC<PromptTemplateProviderProps> = ({
  children,
}) => {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [canManagePrompts, setCanManagePrompts] = useState(false);
  const [loading, setLoading] = useState(true);

  const reloadTemplates = async () => {
    setLoading(true);
    try {
      const data = await getPromptTemplates();
      
      const mappedTemplates = data.templates.map((t: Record<string, unknown>) => ({
        id: t.promptTemplateId as string,
        promptKey: t.promptKey as string,
        name: t.name as string,
        description: (t.description ?? "") as string,
        content: (t.content ?? "") as string,
        category: (t.category ?? "") as string,
        tags: (t.tags as string[]) || [],
        createdAt: t.createdAt as string | undefined,
        updatedAt: t.updatedAt as string | undefined,
        activeVersionId: t.activeVersionId as string | undefined,
        currentVersion: t.currentVersion as number | undefined,
        currentStatus: t.currentStatus as string | undefined,
        totalVersions: (t.totalVersions ?? 0) as number,
        hasPendingDraft: (t.hasPendingDraft ?? false) as boolean,
        pendingDraftVersion: t.pendingDraftVersion as number | undefined,
      }));
      
      setTemplates(mappedTemplates);
      setCanManagePrompts(data.canManagePrompts);
    } catch (error) {
      logError("Failed to load prompt templates:", error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadTemplates();
  }, []);

  const createTemplate = async (template: Omit<PromptTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
    await createPromptTemplate(template);
    await reloadTemplates();
  };

  const updateTemplateHandler = async (template: PromptTemplate) => {
    await updatePromptTemplate({
      promptTemplateId: template.id,
      promptKey: template.promptKey,
      name: template.name,
      description: template.description,
      content: template.content,
      category: template.category,
      tags: template.tags
    });
    await reloadTemplates();
  };

  const deleteTemplateHandler = async (id: string) => {
    await deletePromptTemplate(id);
    await reloadTemplates();
  };

  const getTemplatesByCategory = (category: string): PromptTemplate[] => {
    return templates.filter((t) => t.category === category);
  };

  const findTemplateById = (id: string): PromptTemplate | undefined => {
    return templates.find((t) => t.id === id);
  };

  const searchTemplates = (query: string): PromptTemplate[] => {
    const lowerQuery = query.toLowerCase();
    return templates.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(lowerQuery) ||
        (t.description || "").toLowerCase().includes(lowerQuery) ||
        (t.category || "").toLowerCase().includes(lowerQuery) ||
        t.tags?.some((tag) => tag.toLowerCase().includes(lowerQuery))
    );
  };

  // --- Governance operations ---

  const getVersions = async (templateId: string): Promise<PromptTemplateVersion[]> => {
    try {
      return await getTemplateVersions(templateId);
    } catch (error) {
      logError("Failed to load versions:", error);
      return [];
    }
  };

  const createVersion = async (templateId: string, content: string, changeDescription?: string): Promise<PromptTemplateVersion | null> => {
    if (!content || !content.trim()) {
      throw new Error("Content must not be empty.");
    }
    if (content.length > 50000) {
      throw new Error("Content must not exceed 50,000 characters.");
    }
    if (changeDescription && changeDescription.length > 2000) {
      throw new Error("Change description must not exceed 2,000 characters.");
    }
    try {
      const version = await createTemplateVersion(templateId, content, changeDescription);
      // Full reload to ensure activeVersionId, currentVersion, and currentStatus are
      // consistent with the server state immediately after version creation.
      await reloadTemplates();
      return version;
    } catch (error) {
      logError("Failed to create version:", error);
      return null;
    }
  };

  const changeVersionStatus = async (versionId: string, status: VersionStatus, reason?: string): Promise<boolean> => {
    if (reason && reason.length > 2000) {
      throw new Error("Reason must not exceed 2,000 characters.");
    }
    try {
      await updateVersionStatus(versionId, status, reason);
      // A status change can cascade: approving a draft deprecates the previously-active version,
      // which updates currentVersion, currentStatus, hasPendingDraft, and pendingDraftVersion on
      // the template summary. Computing all these changes client-side without fresh server data
      // would be error-prone, so a full reload is performed here.
      // TODO: If this becomes a performance bottleneck, add a GET /prompt-templates/{id} endpoint
      //       that returns a single template's summary and use that instead of a full reload.
      await reloadTemplates();
      return true;
    } catch (error) {
      logError("Failed to update version status:", error);
      return false;
    }
  };

  const value: PromptTemplateContextType = {
    templates,
    canManagePrompts,
    loading,
    reloadTemplates,
    createTemplate,
    updateTemplate: updateTemplateHandler,
    deleteTemplate: deleteTemplateHandler,
    getTemplatesByCategory,
    findTemplateById,
    searchTemplates,
    getVersions,
    createVersion,
    changeVersionStatus,
  };

  return (
    <PromptTemplateContext.Provider value={value}>
      {children}
    </PromptTemplateContext.Provider>
  );
};

/**
 * Hook to use prompt template context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const usePromptTemplates = (): PromptTemplateContextType => {
  const context = useContext(PromptTemplateContext);
  if (!context) {
    throw new Error(
      "usePromptTemplates must be used within a PromptTemplateProvider"
    );
  }
  return context;
};
